import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, DollarSign, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

export default function MyPaymentPlansSection() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('blkmrkt_token');
      
      // Don't fetch if no token - user not logged in
      if (!token) {
        console.log('[PaymentPlans] No token found, skipping fetch');
        setDebugInfo({ status: 'no_token' });
        setPlans([]);
        setLoading(false);
        return;
      }
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/user/payment-plans`;
      console.log('[PaymentPlans] Fetching from:', apiUrl);
      
      const res = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[PaymentPlans] Response:', res.data);
      setDebugInfo({ 
        status: 'success', 
        url: apiUrl,
        count: res.data?.length || 0 
      });
      
      setPlans(res.data || []);
    } catch (error) {
      console.error('[PaymentPlans] Error:', error.response?.status, error.response?.data || error.message);
      setDebugInfo({ 
        status: 'error', 
        code: error.response?.status,
        message: error.response?.data?.detail || error.message,
        url: `${process.env.REACT_APP_BACKEND_URL}/api/user/payment-plans`
      });
      // Don't show error for auth issues - just show empty
      if (error.response?.status === 401 || error.response?.status === 403) {
        setPlans([]);
      } else {
        setError(error.response?.data?.detail || error.message || 'Failed to load plans');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-tiffany-500/20 text-tiffany-400 border-tiffany-500/30';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled': return 'bg-zinc-700/20 text-zinc-500 border-zinc-700/30';
      case 'defaulted': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
      return new Date() > new Date(dueDate);
    } catch {
      return false;
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    try {
      const due = new Date(dueDate);
      const now = new Date();
      const diffMs = due - now;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="border border-zinc-800 p-6">
        <p className="font-mono text-sm text-zinc-500">LOADING PAYMENT PLANS...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-800 p-6">
        <p className="font-mono text-sm text-red-500">ERROR: {error}</p>
        <Button onClick={fetchPlans} className="mt-2 text-xs">RETRY</Button>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="border border-zinc-800 p-6">
        <p className="font-mono text-sm text-zinc-500">NO PAYMENT PLANS</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {plans.map((plan) => {
        const progress = plan.total_amount > 0 ? (plan.amount_paid / plan.total_amount) * 100 : 0;
        const daysUntil = getDaysUntilDue(plan.next_payment_due);
        const overdue = plan.status === 'active' && isOverdue(plan.next_payment_due);
        
        return (
          <div 
            key={plan.id}
            className={`border p-4 ${getStatusColor(plan.status)}`}
          >
            {/* Header */}
            <div className="flex gap-3 mb-4">
              {plan.product_image && (
                <img 
                  src={plan.product_image.startsWith('data:') ? plan.product_image : 
                       plan.product_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${plan.product_image}` : 
                       plan.product_image}
                  alt={plan.product_name}
                  className="w-16 h-16 object-cover"
                />
              )}
              <div className="flex-1">
                {plan.order_id && (
                  <p className="font-mono text-xs text-tiffany-500 font-bold">{plan.order_id}</p>
                )}
                <p className="font-mono text-sm text-white">{plan.product_name}</p>
                {plan.variant_name && (
                  <p className="font-mono text-[10px] text-zinc-500">{plan.variant_name}</p>
                )}
                <p className="font-mono text-[10px] text-zinc-500 mt-1 uppercase">
                  {plan.status === 'active' && 'ACTIVE PLAN'}
                  {plan.status === 'completed' && 'PAID IN FULL'}
                  {plan.status === 'cancelled' && 'CANCELLED'}
                  {plan.status === 'defaulted' && 'DEFAULTED'}
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-xs font-mono mb-1">
                <span className="text-zinc-400">Progress</span>
                <span className="text-white">${plan.amount_paid?.toFixed(2)} / ${plan.total_amount?.toFixed(2)}</span>
              </div>
              <Progress value={progress} className="h-2 bg-zinc-800" />
            </div>

            {/* Next Payment Due */}
            {plan.status === 'active' && plan.next_payment_due && (
              <div className={`p-3 mb-4 ${overdue ? 'bg-red-500/20 border border-red-500/30' : 'bg-zinc-800/50'}`}>
                <div className="flex items-center gap-2">
                  {overdue ? (
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  ) : (
                    <Clock className="w-4 h-4 text-tiffany-500" />
                  )}
                  <span className="font-mono text-xs text-white">
                    {overdue ? 'PAYMENT OVERDUE' : 'NEXT PAYMENT DUE'}
                  </span>
                </div>
                <p className="font-mono text-sm text-white mt-1">
                  {formatDate(plan.next_payment_due)}
                  {!overdue && daysUntil !== null && (
                    <span className="text-zinc-500 ml-2">({daysUntil} days)</span>
                  )}
                </p>
                <p className="font-mono text-xs text-yellow-500 mt-1">
                  Amount: ${plan.payments?.find(p => !p.paid)?.amount?.toFixed(2) || '0.00'}
                </p>
              </div>
            )}

            {/* Payment Schedule */}
            <div className="space-y-2">
              <p className="font-mono text-[10px] text-zinc-500">PAYMENT SCHEDULE</p>
              {plan.payments?.map((payment, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 text-xs font-mono ${
                    payment.paid ? 'bg-green-500/10' : 'bg-zinc-800/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {payment.paid ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 border border-zinc-600 rounded-full" />
                    )}
                    <span className="text-zinc-400">Payment {payment.payment_number}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-white">${payment.amount?.toFixed(2)}</span>
                    <p className="text-[10px] text-zinc-600">
                      {payment.paid ? `Paid ${formatDate(payment.paid_date)}` : `Due ${formatDate(payment.due_date)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Non-refundable notice */}
            <p className="font-mono text-[10px] text-yellow-600 mt-4 text-center">
              All payments are non-refundable
            </p>
          </div>
        );
      })}
    </div>
  );
}
